import { createColumnHelper } from '@tanstack/react-table';
import { useSelector } from 'react-redux';
import { lt, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { EnterpriseLink, LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { ExportStateEnum, ExportTypeEnum, ExportItem } from '@mews-commander/data-access';
import { Link } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { isCurrentProfileAdmin } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { StatusTag } from '../StatusTag';
import { useExportDownload } from '../useExportDownload';
import { REPORT_TYPE_WITHOUT_DETAILS } from '../../constants';

/**
 * Determines whether a report type should have a detail page based on its report type.
 */
const hasDetailPage = (reportTypeName?: ExportItem['reportTypeName']): boolean => {
    if (!reportTypeName) {
        return true;
    }
    return !REPORT_TYPE_WITHOUT_DETAILS.includes(reportTypeName);
};

const columnHelper = createColumnHelper<ExportItem>();

export const useColumns = () => {
    const exportDownload = useExportDownload();
    const isUserAdmin: boolean = useSelector(isCurrentProfileAdmin);
    const columns = [
        columnHelper.accessor('reportTypeName', {
            header: () => <LocalizedText textKey={TextKey.Export} />,
            cell: (props) =>
                hasDetailPage(props.row.original.reportTypeName) ? (
                    <EnterpriseLink
                        to="Export/Detail/$id"
                        params={{ id: props.row.original.id ?? '' }}
                        inherit
                        standalone
                    >
                        {lt(props.getValue()?.toString() as Mews.TextKey)}
                    </EnterpriseLink>
                ) : (
                    <LocalizedText textKey={props.getValue()?.toString() as Mews.TextKey} />
                ),
        }),
        columnHelper.accessor('scheduleName', {
            header: () => <LocalizedText textKey={TextKey.ExportSchedule} />,
        }),
        columnHelper.accessor((row) => row.localizedOwnerProfile, {
            id: 'localizedOwnerProfile',
            header: () => <LocalizedText textKey={TextKey.Owner} />,
        }),

        columnHelper.accessor('createdUtc', {
            header: () => <LocalizedText textKey={TextKey.Created} />,
            cell: (props) => {
                const value = props.getValue()?.toString() ?? '';
                return value ? <LocalizedDateTimeFromUtc utcString={value} /> : UnicodeEntity.Dash;
            },
        }),
        columnHelper.accessor('startUtc', {
            header: () => <LocalizedText textKey={TextKey.Start} />,
            cell: (props) => {
                const value = props.getValue()?.toString() ?? '';
                return value ? <LocalizedDateTimeFromUtc utcString={value} /> : UnicodeEntity.Dash;
            },
        }),
        columnHelper.accessor('duration', {
            header: () => <LocalizedText textKey={TextKey.Duration} />,
        }),

        columnHelper.accessor('exportState', {
            header: () => <LocalizedText textKey={TextKey.Status} />,
            cell: (props) => (
                <StatusTag note={props.row.original.notes || ''} status={props.getValue() || ExportStateEnum.Pending} />
            ),
        }),
        columnHelper.accessor('fileSize', {
            header: () => <LocalizedText textKey={TextKey.FileSize} />,
            cell: (props) =>
                props.getValue()?.toString() && props.row.original.exportState === ExportStateEnum.Success
                    ? `${Math.ceil((props.getValue() || 0) / 1024)} kB`
                    : UnicodeEntity.Dash,
        }),
        columnHelper.accessor('fileName', {
            header: () => <LocalizedText textKey={TextKey.FileName} />,
            cell: (props) =>
                props.row.original.fileSize ? (
                    <Link
                        onClick={() =>
                            exportDownload.mutate({
                                exportId: props.row.original.id || '',
                                exportType: props.row.original.exportType || ExportTypeEnum.Manual,
                            })
                        }
                        standalone
                    >
                        {props.getValue()}
                    </Link>
                ) : (
                    UnicodeEntity.Dash
                ),
        }),
    ];
    if (!isUserAdmin) {
        return columns.filter((column) => column.id !== 'localizedOwnerProfile');
    }
    return columns;
};
